import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-xl border px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-border-subtle bg-muted text-primary',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/80',
        success:
          'border-success-border bg-success-muted text-success-foreground',
        info: 'border-brand-steel-border bg-brand-steel-light text-brand-steel-dark',
        warning:
          'border-warning-border bg-warning-muted text-warning-foreground',
        error: 'border-error-border bg-error-muted text-error-foreground',
        outline: 'text-foreground',
        dark: 'border-transparent bg-primary text-primary-foreground',
        tertiary: 'border-transparent bg-tertiary text-primary-foreground',
        stone:
          'border-brand-stone-border bg-brand-stone-light text-brand-stone-dark',
        caramel:
          'border-brand-caramel-border bg-brand-caramel-light text-brand-caramel-dark',
        mustard:
          'border-brand-mustard-border bg-brand-mustard-light text-brand-mustard-dark',
        sage: 'border-brand-sage-border bg-brand-sage-light text-brand-sage-dark',
        steel:
          'border-brand-steel-border bg-brand-steel-light text-brand-steel-dark',
        mauve:
          'border-brand-mauve-border bg-brand-mauve-light text-brand-mauve-dark',
        blush:
          'border-brand-blush-border bg-brand-blush-light text-brand-blush-dark',
        terracotta:
          'border-brand-terracotta-border bg-brand-terracotta-light text-brand-terracotta-dark',
      },
      pill: {
        true: 'h-6 rounded-full font-medium',
      },
    },
    defaultVariants: {
      variant: 'default',
      pill: false,
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  noBorder?: boolean;
}

export type BadgeVariant = NonNullable<BadgeProps['variant']>;

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({className, variant, pill, noBorder, ...props}, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          badgeVariants({variant, pill}),
          noBorder && 'border-0',
          className,
        )}
        {...props}
      />
    );
  },
);
Badge.displayName = 'Badge';

export {Badge, badgeVariants};
